import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Play, ShoppingCart, ArrowRight, Loader2, RefreshCw, WifiOff } from 'lucide-react';
import { fetchShared, readCached } from '../utils/navDataCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_MAX_AGE = 60000;

function accessCacheKey(moduleKey) {
  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { /* signed out */ }
  return `module-access:${user.id || 'anon'}:${user.institute_id || ''}:${moduleKey}`;
}

/** Only grants are reused; a locked result is always re-checked (e.g. right after a purchase). */
function fetchAccess(moduleKey, force = false) {
  const cached = readCached(accessCacheKey(moduleKey));
  if (cached && !cached.data?.has_access) force = true;
  return fetchShared(
    accessCacheKey(moduleKey),
    () => axios.get(`${API}/module-store/check-access/${moduleKey}`, { timeout: 10000 }).then((res) => res.data),
    { maxAge: ACCESS_MAX_AGE, force },
  );
}

/** Last known grant for any key, so revisiting a page renders without waiting. */
function cachedGrant(keys) {
  for (const key of keys) {
    const cached = readCached(accessCacheKey(key));
    if (cached?.data?.has_access) return cached.data;
  }
  return null;
}

/**
 * Grants access when any of `moduleKeys` (or the single `moduleKey`) is active.
 * Keys are checked in parallel and shared for a minute across pages; the backend still
 * enforces module access on every API call.
 */
const RETRY_DELAYS_MS = [1500, 4000];
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function ModuleGuard({ moduleKey, moduleKeys, moduleName, children }) {
  const keys = moduleKeys && moduleKeys.length ? moduleKeys : [moduleKey];
  const keysParam = keys.join(',');
  const [access, setAccess] = useState(() => cachedGrant(keys));
  const [loading, setLoading] = useState(() => !cachedGrant(keys));
  const [startingTrial, setStartingTrial] = useState(false);
  const navigate = useNavigate();
  const accessRef = useRef(access);
  accessRef.current = access;

  /**
   * Locked only when the server says so. A failed check (server restarting, slow network,
   * timeout) never takes away access already granted, and on first load is retried before
   * showing "could not check" (with a retry button), not the locked / trial screen.
   */
  const checkAccess = async (force = false) => {
    const list = keysParam.split(',');
    for (let attempt = 0; ; attempt += 1) {
      const results = await Promise.all(list.map((key) => fetchAccess(key, force || attempt > 0).catch(() => null)));
      const granted = results.find((result) => result?.has_access);
      if (granted) {
        setAccess(granted);
        break;
      }
      // Every key answered "no access": really locked (e.g. plan changed, trial ended)
      if (results.every(Boolean)) {
        setAccess(results[0]);
        break;
      }
      if (accessRef.current?.has_access) break; // keep the grant we have
      if (attempt >= RETRY_DELAYS_MS.length) {
        setAccess({ has_access: false, reason: 'check_failed' });
        break;
      }
      await wait(RETRY_DELAYS_MS[attempt]);
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAccess();
    const onFocus = () => checkAccess();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [keysParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const res = await axios.post(`${API}/module-store/start-trial`, { module_id: keys[0] }, { timeout: 20000 });
      toast.success(res.data?.message || 'Free trial activated successfully! Enjoy 7 days full access.');
      setAccess({ has_access: true, source: 'trial' });
      fetchAccess(keys[0], true).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start trial directly. Opening Module Store...');
      navigate('/institute-panel/module-store');
    } finally {
      setStartingTrial(false);
    }
  };

  if (loading) return null;
  if (access?.has_access) return children;

  if (access?.reason === 'check_failed') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 animate-in fade-in" data-testid="module-check-failed">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5 dark:bg-amber-500/15">
            <WifiOff className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2 dark:text-white">{moduleName || 'Module'} access check nahi ho paya</h2>
          <p className="text-sm text-slate-500 mb-6 leading-relaxed dark:text-slate-400">
            Server se jawab nahi mila (internet ya server busy). Aapka module band nahi hua hai — thodi der mein dobara try karein.
          </p>
          <button
            type="button"
            onClick={() => { setLoading(true); checkAccess(true); }}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6 animate-in fade-in">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-2xs dark:bg-slate-800">
          <Lock className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2 dark:text-white" data-testid="module-locked-title">
          {moduleName || 'Module'} is Locked
        </h2>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed dark:text-slate-400">
          This module requires activation. Start a free 7-day trial or purchase it from the Module Store to unlock all features.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleStartTrial}
            disabled={startingTrial}
            data-testid="module-locked-trial-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-50 text-amber-800 border border-amber-300 rounded-xl text-sm font-bold hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-60 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30 dark:hover:bg-amber-500/20"
          >
            {startingTrial ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Activating Trial...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 fill-amber-700 text-amber-700 dark:text-amber-300" /> Start Free Trial
              </>
            )}
          </button>
          <button
            onClick={() => navigate('/institute-panel/module-store')}
            data-testid="module-locked-buy-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer shadow-sm shadow-indigo-600/20"
          >
            <ShoppingCart className="w-4 h-4" /> Go to Module Store <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
